import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import marketDataService from '../services/marketDataService';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch real market data
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch a mix of cryptocurrencies and stocks for the ticker
        const [cryptoData, stockData] = await Promise.all([
          marketDataService.fetchTopCryptocurrencies(3),
          marketDataService.fetchTopStocks()
        ]);
        
        // Combine and format data for the ticker
        const formattedData = [
          ...cryptoData.map((item, index) => ({
            id: `crypto-${index}`,
            symbol: `${item.symbol}/USD`,
            price: item.price,
            change: item.changePercent
          })),
          ...stockData.slice(0, 3).map((item, index) => ({
            id: `stock-${index}`,
            symbol: item.symbol,
            price: item.price,
            change: item.changePercent
          }))
        ];
        
        setMarketData(formattedData);
      } catch (err) {
        console.error('Error fetching market data for ticker:', err);
        setError('Failed to load market data');
        // Fallback to static data if API fails
        setMarketData([
          { id: 1, symbol: 'BTC/USD', price: 57320.50, change: 2.35 },
          { id: 2, symbol: 'ETH/USD', price: 3420.75, change: -1.25 },
          { id: 3, symbol: 'XAU/USD', price: 1935.60, change: 0.17 },
          { id: 4, symbol: 'SPX', price: 4567.80, change: 0.33 },
          { id: 5, symbol: 'EUR/USD', price: 1.0825, change: 0.14 },
          { id: 6, symbol: 'AAPL', price: 175.25, change: 0.72 }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
    
    // Set up interval to refresh data every 30 seconds
    const interval = setInterval(fetchMarketData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const linkVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } }
  };

  const floatAnimation = {
    y: [0, -3, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const publicNavItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Brokers', path: '/brokers' },
    { name: 'Who We Are', path: '/who-we-are' },
    { name: 'Contact', path: '/contact' },
    { name: 'Login', path: '/login' },
    { name: 'Register', path: '/register' }
  ];

  const userNavItems = [
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'Brokers', path: '/brokers' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <motion.header 
      className={`site-header ${isScrolled ? 'scrolled' : ''}`}
      initial="hidden"
      animate="visible"
      variants={navVariants}
    >
      {/* Live Market Ticker */}
      <div className="market-ticker">
        <div className="ticker-container">
          {!loading && !error && marketData.length > 0 && (
            <motion.div 
              className="ticker-content"
              initial={{ x: 0 }}
              animate={{ x: [-marketData.length * 150, 0] }}
              transition={{ 
                repeat: Infinity,
                repeatType: "loop",
                duration: marketData.length * 3,
                ease: "linear"
              }}
            >
              {[...marketData, ...marketData].map((item, index) => (
                <motion.div 
                  key={`${item.id}-${index}`} 
                  className="ticker-item"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <span className="ticker-symbol">{item.symbol}</span>
                  <span className="ticker-price">
                    {Number(item.price).toLocaleString(undefined,
                      item.symbol.includes('EUR') || item.symbol.includes('USD') && item.price < 1
                        ? { minimumFractionDigits: 4, maximumFractionDigits: 4 }
                        : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </span>
                  <span className={`ticker-change ${item.change >= 0 ? 'positive' : 'negative'}`}>
                    {item.change >= 0 ? '▲' : '▼'} {Math.abs(item.change).toFixed(2)}%
                  </span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
      
      <div className="container">
        <motion.div 
          className="logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/">UPM Traders</Link>
        </motion.div>
        
        <div 
          className="menu-toggle" 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          whileTap={{ scale: 0.95 }}
        >
          <motion.span 
            animate={{ 
              rotate: isMobileMenuOpen ? 45 : 0,
              y: isMobileMenuOpen ? 6 : 0 
            }}
          />
          <motion.span 
            animate={{ 
              opacity: isMobileMenuOpen ? 0 : 1 
            }}
          />
          <motion.span 
            animate={{ 
              rotate: isMobileMenuOpen ? -45 : 0,
              y: isMobileMenuOpen ? -6 : 0 
            }}
          />
        </div>
        
        <motion.nav 
          className={`main-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ul>
            {(isAuthenticated ? userNavItems : publicNavItems).map((item, index) => (
              <motion.li 
                key={item.name} 
                variants={linkVariants} 
                whileHover="hover"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.span
                  whileHover={{ 
                    color: "#D4AF37",
                    transition: { duration: 0.3 }
                  }}
                >
                  <Link 
                    to={item.path} 
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </motion.span>
              </motion.li>
            ))}
            {isAuthenticated && (
              <motion.li 
                variants={linkVariants} 
                whileHover="hover"
                whileTap={{ scale: 0.95 }}
              >
                <motion.span
                  whileHover={{ 
                    color: "#D4AF37",
                    transition: { duration: 0.3 }
                  }}
                >
                  <Link to="/" onClick={() => setIsAuthenticated(false)}>
                    Logout
                  </Link>
                </motion.span>
              </motion.li>
            )}
          </ul>
        </motion.nav>
      </div>
    </motion.header>
  );
};

export default Header;